/**
 * Discrete summary sub-views for the activity detail surfaces.
 *
 * Each export renders ONE self-contained block (a single card or table).
 * The drawer and full-page detail compose them in their own layouts —
 * the drawer stacks them in a flex column, the page distributes them
 * across a KPI strip + body cards. There is no aggregator wrapper and
 * no `hide*` flags: each consumer renders the blocks it wants in the
 * order it wants.
 */

import { Table } from 'reactstrap';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { MetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Leaf, MapPin, ShoppingCart } from 'lucide-react';
import { FUEL_TYPE_LABELS, GAS_TYPE_LABELS, PROCESS_TYPE_LABELS, VEHICLE_TYPE_LABELS } from '@g17eco/emissions-config';
import { type ActivityRecord, type EmissionCalculation, Scope2Method } from '../../types/emissions';
import { SCOPE_COLORS, SCOPE_LUCIDE_ICONS, SCOPE_TAILWIND } from '../../config/scopeBranding';
import { formatCO2e, formatCO2eAutoUnit, formatDateShort, SCOPE_LABELS, STATUS_CONFIG } from './formatters';

export type SummaryActivityData = Pick<
  ActivityRecord,
  | 'activityType'
  | 'quantity'
  | 'unit'
  | 'region'
  | 'periodStart'
  | 'periodEnd'
  | 'scope'
  | 'fuelType'
  | 'vehicleType'
  | 'refrigerantType'
  | 'processType'
  | 'regionFallback'
  | 'yearFallback'
  | 'name'
  | 'dataSource'
  | 'notes'
>;

interface FormatProps {
  showInTonnes: boolean;
  decimalPlaces: number;
}

// ─── CO₂e headline ───────────────────────────────────────────────────────

interface ActivityCo2eHeadlineProps extends FormatProps {
  calculation: EmissionCalculation;
}

/**
 * CO₂e headline as a MetricCard. For Scope 2 calculations with dual
 * reporting we render the LB / MB pair as two stacked MetricCards;
 * otherwise a single Scope-coloured MetricCard.
 */
export const ActivityCo2eHeadline = ({ calculation, showInTonnes, decimalPlaces }: ActivityCo2eHeadlineProps) => {
  const statusConfig = STATUS_CONFIG[calculation.status];
  const scopeStyle = SCOPE_TAILWIND[calculation.scope];
  const ScopeIcon = SCOPE_LUCIDE_ICONS[calculation.scope];
  const scopeLabel = SCOPE_LABELS[calculation.scope];

  if (calculation.locationBasedCo2e !== undefined || calculation.marketBasedCo2e !== undefined) {
    return (
      <div>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>Dual Reporting</p>
        <div className='tw:grid tw:gap-3'>
          {calculation.locationBasedCo2e !== undefined && (
            <MetricCard
              label='Location-Based'
              value={formatCO2e(calculation.locationBasedCo2e, showInTonnes, decimalPlaces)}
              icon={<MapPin className='tw:size-5 tw:text-blue-600' />}
              iconBg='tw:bg-blue-500/10'
              borderColor='blue-500'
              subtext={calculation.scope2Method === Scope2Method.LocationBased ? 'Selected method' : undefined}
            />
          )}
          {calculation.marketBasedCo2e !== undefined && (
            <MetricCard
              label='Market-Based'
              value={formatCO2e(calculation.marketBasedCo2e, showInTonnes, decimalPlaces)}
              icon={<ShoppingCart className='tw:size-5 tw:text-emerald-600' />}
              iconBg='tw:bg-emerald-500/10'
              borderColor='emerald-500'
              subtext={calculation.scope2Method === Scope2Method.MarketBased ? 'Selected method' : undefined}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <MetricCard
      label={`${scopeLabel} Emissions`}
      value={formatCO2e(calculation.co2e, showInTonnes, decimalPlaces)}
      icon={ScopeIcon ? <ScopeIcon className={cn('tw:size-5', scopeStyle?.icon)} /> : <></>}
      iconBg={scopeStyle?.iconBg}
      borderColor={scopeStyle?.border}
      valueColor={SCOPE_COLORS[calculation.scope]}
      subtext={statusConfig.label}
    />
  );
};

// ─── Gas breakdown ───────────────────────────────────────────────────────

interface ActivityGasBreakdownCardProps extends FormatProps {
  calculation: EmissionCalculation;
}

export const hasGasBreakdownData = (calculation: EmissionCalculation): boolean =>
  calculation.co2 !== undefined || calculation.ch4 !== undefined || calculation.n2o !== undefined;

/**
 * Per-gas contribution card. Renders nothing if the calculation has no
 * constituent_gases data — guard at callsite via `hasGasBreakdownData`.
 */
export const ActivityGasBreakdownCard = ({ calculation, showInTonnes, decimalPlaces }: ActivityGasBreakdownCardProps) => {
  const scopeStyle = SCOPE_TAILWIND[calculation.scope];

  return (
    <Card className={cn('tw:border-0', scopeStyle?.gradient)}>
      <CardContent className='tw:pt-4 tw:pb-4'>
        <p className='tw:text-sm tw:mb-3 tw:text-muted-foreground'>Gas Breakdown</p>
        {calculation.co2 !== undefined && (
          <div className='tw:flex tw:justify-between tw:items-center tw:mb-2'>
            <span className='tw:text-sm'>CO₂</span>
            <span className='tw:text-sm tw:font-semibold'>
              {formatCO2eAutoUnit(calculation.co2, showInTonnes, decimalPlaces)}
            </span>
          </div>
        )}
        {calculation.ch4 !== undefined && (
          <div className='tw:flex tw:justify-between tw:items-center tw:mb-2'>
            <span className='tw:text-sm'>CH₄</span>
            <span className='tw:text-sm tw:font-semibold'>
              {formatCO2eAutoUnit(calculation.ch4, showInTonnes, decimalPlaces)}
            </span>
          </div>
        )}
        {calculation.n2o !== undefined && (
          <div className='tw:flex tw:justify-between tw:items-center'>
            <span className='tw:text-sm'>N₂O</span>
            <span className='tw:text-sm tw:font-semibold'>
              {formatCO2eAutoUnit(calculation.n2o, showInTonnes, decimalPlaces)}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Supplementary (T&D / WTT / WTT-of-T&D) ──────────────────────────────

interface ActivitySupplementaryCardProps extends FormatProps {
  calculation: EmissionCalculation;
}

export const hasSupplementaryData = (calculation: EmissionCalculation): boolean =>
  calculation.tdCo2e !== undefined ||
  calculation.wttCo2e !== undefined ||
  calculation.wttOfTdCo2e !== undefined;

export const ActivitySupplementaryCard = ({ calculation, showInTonnes, decimalPlaces }: ActivitySupplementaryCardProps) => (
  <Card className='tw:border-l-[3px] tw:border-l-black/20'>
    <CardContent className='tw:pt-4 tw:pb-4'>
      <p className='tw:text-sm tw:text-muted-foreground tw:mb-3'>Supplementary — Scope 3</p>
      {calculation.tdCo2e !== undefined && (
        <div className='tw:flex tw:justify-between tw:items-center tw:mb-2'>
          <span className='tw:text-sm'>T&amp;D Losses (Scope 3.3)</span>
          <span className='tw:text-sm tw:font-semibold'>
            {formatCO2e(calculation.tdCo2e, showInTonnes, decimalPlaces)}
          </span>
        </div>
      )}
      {calculation.wttCo2e !== undefined && (
        <div className='tw:flex tw:justify-between tw:items-center tw:mb-2'>
          <span className='tw:text-sm'>WTT Upstream</span>
          <span className='tw:text-sm tw:font-semibold'>
            {formatCO2e(calculation.wttCo2e, showInTonnes, decimalPlaces)}
          </span>
        </div>
      )}
      {calculation.wttOfTdCo2e !== undefined && (
        <div className='tw:flex tw:justify-between tw:items-center'>
          <span className='tw:text-sm'>WTT of T&amp;D</span>
          <span className='tw:text-sm tw:font-semibold'>
            {formatCO2e(calculation.wttOfTdCo2e, showInTonnes, decimalPlaces)}
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

// ─── Biogenic CO₂ memo ───────────────────────────────────────────────────

interface ActivityBiogenicCardProps extends FormatProps {
  calculation: EmissionCalculation;
}

/** Returns null if the calculation has no biogenic CO₂ value. */
export const ActivityBiogenicCard = ({ calculation, showInTonnes, decimalPlaces }: ActivityBiogenicCardProps) => {
  if (calculation.biogenicCo2e === undefined) return null;
  return (
    <MetricCard
      label='Biogenic CO₂'
      value={formatCO2e(calculation.biogenicCo2e, showInTonnes, decimalPlaces)}
      icon={<Leaf className='tw:size-5 tw:text-amber-600' />}
      iconBg='tw:bg-amber-500/10'
      borderColor='amber-300'
      subtext='Memo item — outside scopes (GRI 305-1 / CSRD)'
    />
  );
};

// ─── Activity input table ────────────────────────────────────────────────

interface ActivityInputTableProps {
  activityData: SummaryActivityData;
  reportingLevelLabel?: React.ReactNode;
}

/**
 * The activity's user-input dimensions as a flat key/value table.
 * Pure presentational — caller owns the surrounding heading or card.
 */
export const ActivityInputTable = ({ activityData, reportingLevelLabel }: ActivityInputTableProps) => (
  <Table size='sm' borderless className='mb-0'>
    <tbody>
      {activityData.name && (
        <tr>
          <td className='tw:text-lg tw:font-semibold' colSpan={2}>
            {activityData.name}
          </td>
        </tr>
      )}
      {activityData.dataSource && (
        <tr>
          <td colSpan={2}>
            <Badge variant='secondary' className='tw:text-md'>{activityData.dataSource}</Badge>
          </td>
        </tr>
      )}
      {activityData.notes && (
        <tr>
          <td className='text-ThemeTextLight' colSpan={2} style={{ whiteSpace: 'pre-wrap' }}>{activityData.notes}</td>
        </tr>
      )}
      <tr>
        <td colSpan={2} className='p-0'>
          <hr className='my-2' />
        </td>
      </tr>
      <tr>
        <td className='text-ThemeTextLight '>Quantity</td>
        <td className='text-end pe-0'>
          {activityData.quantity.toLocaleString()} {activityData.unit}
        </td>
      </tr>
      <tr>
        <td className='text-ThemeTextLight '>Activity Type</td>
        <td className='text-end pe-0 text-capitalize'>{activityData.activityType.replace(/_/g, ' ')}</td>
      </tr>
      {activityData.vehicleType && (
        <tr>
          <td className='text-ThemeTextLight '>Vehicle Type</td>
          <td className='text-end pe-0'>{VEHICLE_TYPE_LABELS[activityData.vehicleType]}</td>
        </tr>
      )}
      {activityData.fuelType && (
        <tr>
          <td className='text-ThemeTextLight '>Fuel Type</td>
          <td className='text-end pe-0'>{FUEL_TYPE_LABELS[activityData.fuelType]}</td>
        </tr>
      )}
      {activityData.refrigerantType && (
        <tr>
          <td className='text-ThemeTextLight '>Gas / Refrigerant</td>
          <td className='text-end pe-0'>{GAS_TYPE_LABELS[activityData.refrigerantType]}</td>
        </tr>
      )}
      {activityData.processType && (
        <tr>
          <td className='text-ThemeTextLight '>Process Type</td>
          <td className='text-end pe-0'>{PROCESS_TYPE_LABELS[activityData.processType]}</td>
        </tr>
      )}
      <tr>
        <td colSpan={2} className='p-0'>
          <hr className='my-2' />
        </td>
      </tr>
      <tr>
        <td className='text-ThemeTextLight '>Region</td>
        <td className='text-end pe-0'>{activityData.region}</td>
      </tr>
      <tr>
        <td className='text-ThemeTextLight '>Region Fallback</td>
        <td className='text-end pe-0'>
          <SimpleTooltip
            text={
              activityData.regionFallback
                ? 'If no emission factor matches the exact region, the system will search broader regions'
                : 'Only exact region matches are used — no fallback to broader regions'
            }
          >
            <Badge variant='secondary'>{activityData.regionFallback ? 'Allowed' : 'Not allowed'}</Badge>
          </SimpleTooltip>
        </td>
      </tr>
      <tr>
        <td className='text-ThemeTextLight '>Year Fallback</td>
        <td className='text-end pe-0'>
          <SimpleTooltip
            text={
              activityData.yearFallback
                ? 'Only exact year matches are used — no fallback to other years'
                : 'If no emission factor matches the activity year, the system will use the closest available year'
            }
          >
            <Badge variant='secondary'>{activityData.yearFallback ? 'Allowed' : 'Not allowed'}</Badge>
          </SimpleTooltip>
        </td>
      </tr>
      <tr>
        <td className='text-ThemeTextLight '>Period</td>
        <td className='text-end pe-0'>
          {formatDateShort(activityData.periodStart)} - {formatDateShort(activityData.periodEnd)}
        </td>
      </tr>
      <tr>
        <td className='text-ThemeTextLight ' style={{ width: '40%' }}>
          Scope
        </td>
        <td className='text-end pe-0'>{SCOPE_LABELS[activityData.scope]}</td>
      </tr>
      {reportingLevelLabel && (
        <tr>
          <td className='text-ThemeTextLight '>Reporting Level</td>
          <td className='text-end pe-0'>{reportingLevelLabel}</td>
        </tr>
      )}
    </tbody>
  </Table>
);
